import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { Exercise } from "../../../shared/src";
import { ApiError, addWorkoutSet } from "../api-client";

interface AddExercisePickerProps {
  workoutId: number;
  catalog: Exercise[];
  excluded: Set<number>;
  onAdded: (exerciseId: number) => void;
}

export function AddExercisePicker({
  workoutId,
  catalog,
  excluded,
  onAdded,
}: AddExercisePickerProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [exerciseId, setExerciseId] = useState<number | "">("");
  const [weight, setWeight] = useState("");
  const [reps, setReps] = useState("");
  const [error, setError] = useState<string | null>(null);

  const options = catalog.filter(
    (ex) => !ex.archived && !excluded.has(ex.id),
  );

  const addMutation = useMutation({
    mutationFn: (input: {
      exercise_id: number;
      weight_kg: number;
      reps: number;
    }) => addWorkoutSet(workoutId, input),
    onSuccess: async (_set, input) => {
      await queryClient.invalidateQueries({
        queryKey: ["workoutSets", workoutId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["workoutExercises", workoutId],
      });
      setExerciseId("");
      setWeight("");
      setReps("");
      setOpen(false);
      onAdded(input.exercise_id);
    },
  });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (exerciseId === "") {
      setError("Pick an exercise.");
      return;
    }
    const w = Number(weight);
    const r = Number(reps);
    if (!Number.isFinite(w) || w < 0) {
      setError("Weight must be 0 or greater.");
      return;
    }
    if (!Number.isInteger(r) || r < 1) {
      setError("Reps must be a positive whole number.");
      return;
    }
    setError(null);
    try {
      await addMutation.mutateAsync({
        exercise_id: exerciseId,
        weight_kg: w,
        reps: r,
      });
    } catch (err) {
      setError(messageFor(err));
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={options.length === 0}
        className="w-full rounded-md border border-dashed border-slate-700 px-4 py-3 text-sm text-slate-300 hover:bg-slate-800 disabled:opacity-50"
      >
        {options.length === 0
          ? "All exercises already in this workout"
          : "+ Add exercise"}
      </button>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      aria-label="add exercise"
      className="space-y-2 rounded-lg border border-slate-700 bg-slate-900 p-4"
    >
      <label className="block text-xs text-slate-400">
        <span>Exercise</span>
        <select
          value={exerciseId === "" ? "" : String(exerciseId)}
          onChange={(e) =>
            setExerciseId(e.target.value === "" ? "" : Number(e.target.value))
          }
          aria-label="exercise"
          className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-3 text-base"
        >
          <option value="">— pick an exercise —</option>
          {options.map((ex) => (
            <option key={ex.id} value={ex.id}>
              {ex.name}
              {ex.muscle_group ? ` (${ex.muscle_group})` : ""}
            </option>
          ))}
        </select>
      </label>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col text-xs text-slate-400">
          <span>Weight (kg)</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.5"
            min="0"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            aria-label="weight kg"
            className="mt-1 w-24 rounded-md border border-slate-700 bg-slate-950 px-3 py-3 text-base"
          />
        </label>
        <label className="flex flex-col text-xs text-slate-400">
          <span>Reps</span>
          <input
            type="number"
            inputMode="numeric"
            step="1"
            min="1"
            value={reps}
            onChange={(e) => setReps(e.target.value)}
            aria-label="reps"
            className="mt-1 w-20 rounded-md border border-slate-700 bg-slate-950 px-3 py-3 text-base"
          />
        </label>
        <button
          type="submit"
          disabled={addMutation.isPending}
          className="h-12 rounded-md bg-orange-600 px-4 text-sm font-medium text-white hover:bg-orange-500 disabled:opacity-50"
        >
          Add
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
          className="h-12 rounded-md border border-slate-700 px-3 text-sm hover:bg-slate-800"
        >
          Cancel
        </button>
      </div>
      {error && (
        <p className="text-sm text-red-400" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

function messageFor(err: unknown): string {
  if (
    err instanceof ApiError &&
    (err.code === "BAD_REQUEST" ||
      err.code === "UNPROCESSABLE_ENTITY" ||
      err.code === "CONFLICT")
  ) {
    return err.message;
  }
  return "Something went wrong. Try again.";
}
